import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import WorkflowApi from "../../api/WorkflowApi";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import { isMobile } from "react-device-detect";
import { getEntitlementsSection } from "../../utils/entitlements";
import {
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  InputGroup,
} from "react-bootstrap";
import {
  DatatableWrapper,
  TableBody,
  TableHeader,
  PaginationOptions,
  Pagination,
} from "react-bs-datatable";
import { FaPlus, FaBolt, FaSearch, FaEdit, FaTrash } from "react-icons/fa";

function workflowTriggerEventLabel(row) {
  const raw = row.trigger_event ?? row.config?.eventTiming ?? "";
  const v = String(raw).trim().toLowerCase();
  if (v === "before_save" || v === "before") return "Before Save";
  return "After Save";
}

const WorkflowList = () => {
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filterObjectName, setFilterObjectName] = useState("");
  const [filterStatus, setFilterStatus] = useState(""); // '' | 'active' | 'inactive'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [objectConfigList, setObjectConfigList] = useState([]);

  const workflowEnt = getEntitlementsSection("workflow", {});

  const objectLabelByApiName = useMemo(() => {
    const m = new Map();
    for (const o of objectConfigList || []) {
      if (!o?.object_name) continue;
      const label =
        (o.object_label && String(o.object_label).trim()) || o.object_name;
      const key = String(o.object_name);
      m.set(key, label);
      m.set(key.toLowerCase(), label);
    }
    return m;
  }, [objectConfigList]);

  const resolveObjectLabel = (apiName) => {
    if (apiName == null || apiName === "") return "";
    const s = String(apiName);
    return (
      objectLabelByApiName.get(s) ??
      objectLabelByApiName.get(s.toLowerCase()) ??
      s
    );
  };

  const objectNameOptions = useMemo(() => {
    const names = new Set();
    (body || []).forEach((w) => {
      if (w.object_name) names.add(w.object_name);
    });
    return Array.from(names).sort((a, b) => {
      const la = resolveObjectLabel(a).toLowerCase();
      const lb = resolveObjectLabel(b).toLowerCase();
      if (la !== lb) return la.localeCompare(lb);
      return a.localeCompare(b);
    });
  }, [body, objectLabelByApiName]);

  const filteredBody = useMemo(() => {
    let rows = body || [];
    if (filterObjectName) {
      rows = rows.filter((w) => w.object_name === filterObjectName);
    }
    if (filterStatus === "active") {
      rows = rows.filter((w) => !!w.is_active);
    } else if (filterStatus === "inactive") {
      rows = rows.filter((w) => !w.is_active);
    }
    const q = searchText.trim().toLowerCase();
    if (q) {
      rows = rows.filter((w) => {
        const name = String(w.workflow_name || "").toLowerCase();
        const obj = String(w.object_name || "").toLowerCase();
        const ev = String(w.event_type || "").toLowerCase();
        const objLabel = String(resolveObjectLabel(w.object_name) || "").toLowerCase();
        return (
          name.includes(q) || obj.includes(q) || ev.includes(q) || objLabel.includes(q)
        );
      });
    }
    return rows;
  }, [body, filterObjectName, filterStatus, searchText, objectLabelByApiName]);

  useEffect(() => {
    if (workflowEnt.allowViewWorkflow === false) {
      navigate("/setup", { replace: true });
      return;
    }
    fetchWorkflows();
    (async () => {
      try {
        const res = await ObjectConfigApi.fetchObjectConfigs();
        if (res?.success && Array.isArray(res.data)) {
          setObjectConfigList(res.data);
        }
      } catch (e) {
        console.error("Failed to load object labels for workflow list", e);
      }
    })();
  }, [navigate, workflowEnt.allowViewWorkflow]);

  async function fetchWorkflows() {
    try {
      const result = await WorkflowApi.fetchWorkflows();
      if (result?.data) {
        setBody(result.data);
      }
    } catch (error) {
      console.error("Failed to fetch workflows", error);
    }
  }

  const handleEdit = (workflow) => {
    navigate(`/setup/workflows/${workflow.id}/edit`);
  };

  const handleDeleteClick = (workflow) => {
    setSelectedWorkflow(workflow);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWorkflow) return;

    setDeleting(true);
    try {
      await WorkflowApi.deleteWorkflow(selectedWorkflow.id);
      setShowDeleteModal(false);
      setSelectedWorkflow(null);
      // Refresh the workflows list
      fetchWorkflows();
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      alert("Failed to delete the workflow. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedWorkflow(null);
  };

  const header = [];
  if (!isMobile) {
    header.push(
      {
        title: "Workflow Name",
        prop: "workflow_name",
        isFilterable: false,
        cell: (row) => (
          <Link to={`/setup/workflows/${row.id}`} state={row} className="text-decoration-none fw-semibold">
            <FaBolt className="me-2 text-warning" size={14} />
            {row.workflow_name}
          </Link>
        ),
      },
      {
        title: "Object",
        prop: "object_name",
        isFilterable: false,
        cell: (row) => resolveObjectLabel(row.object_name),
      },
      {
        title: "Event",
        prop: "event_type",
        isFilterable: false,
      },
      {
        title: "Event Type",
        prop: "trigger_event",
        isFilterable: false,
        cell: (row) => workflowTriggerEventLabel(row),
      },
      {
        title: "Status",
        prop: "is_active",
        isFilterable: false,
        cell: (row) => (
          <Badge bg={row.is_active ? "success" : "secondary"}>
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      ...(workflowEnt.allowCreateWorkflow !== false
        ? [
            {
              title: "Actions",
              prop: "actions",
              cell: (row) => (
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleEdit(row)}
                    title="Edit"
                  >
                    <FaEdit />
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeleteClick(row)}
                    title="Delete"
                  >
                    <FaTrash />
                  </Button>
                </div>
              ),
            },
          ]
        : []),
    );
  } else {
    header.push({
      title: "Info",
      prop: "workflow_name",
      isFilterable: false,
      cell: (row) => (
        <div className="mobilecard">
          <Link to={`/setup/workflows/${row.id}`} state={row}>
            <FaBolt className="me-2 text-warning" size={14} />
            {row.workflow_name}
          </Link>
          <span>Object: {resolveObjectLabel(row.object_name)}</span>
          <span>Event: {row.event_type}</span>
          <span>Event Type: {workflowTriggerEventLabel(row)}</span>
          <Badge bg={row.is_active ? "success" : "secondary"} className="mt-2">
            {row.is_active ? "Active" : "Inactive"}
          </Badge>
          {workflowEnt.allowCreateWorkflow !== false && (
            <div className="d-flex gap-2 mt-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handleEdit(row)}
                title="Edit"
              >
                <FaEdit className="me-1" />
                Edit
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDeleteClick(row)}
                title="Delete"
              >
                <FaTrash className="me-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
      ),
    });
  }

  const paginationOptionsLabels = {
    beforeSelect: "Per page",
  };

  const paginationOptionsClasses = {
    formGroup: "mb-0 w-100",
    formText: "small text-muted mb-1",
    formControl: "form-select-sm",
  };

  return (
    <div className="workflow-manager">
      <style jsx>{`
        .workflow-manager .header-flex {
          display: flex !important;
          align-items: center !important;
          margin-bottom: 1rem !important;
        }
        .workflow-manager .header-icon {
          margin-right: 0.5rem !important;
        }
        .workflow-manager .card-header-flex {
          display: flex !important;
          justify-content: space-between !important;
          align-items: center !important;
        }
        .workflow-manager .filter-flex {
          display: flex !important;
          align-items: flex-start !important;
          margin-bottom: 1rem !important;
        }
      `}</style>

      {/* Header Section */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="header-flex">
            <div className="header-icon">
              <div className="bg-warning bg-opacity-10 p-3 rounded-circle d-inline-flex">
                <FaBolt size={32} className="text-warning" />
              </div>
            </div>
            <div>
              <h2 className="mb-1 fw-bold">Workflow Manager</h2>
              <p className="text-muted mb-0">
                Automate actions like sending emails, creating tasks, updating fields, and more based on record changes
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Workflows List */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white border-bottom">
          <div className="card-header-flex">
            <h5 className="mb-0 fw-semibold">
              <FaBolt className="me-2 text-warning" />
              <span className="me-2 text-dark"> All Workflows</span>
              <Badge bg="secondary" className="ms-2">
                {filteredBody.length === body.length
                  ? body.length
                  : `${filteredBody.length} / ${body.length}`}
              </Badge>
            </h5>
            {getEntitlementsSection("workflow", {}).allowCreateWorkflow !== false && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate("/setup/workflows/create")}
              >
                <FaPlus className="me-2" size={12} />
                New Workflow
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-3">
          <DatatableWrapper
            body={filteredBody}
            headers={header}
            totalRows={filteredBody.length}
            paginationOptionsProps={{
              initialState: {
                rowsPerPage: 10,
                options: [5, 10, 15, 20],
              },
            }}
          >
            <Row className="g-2 filter-flex">
              <Col xs={12} md={6} lg={3}>
                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                <InputGroup size="sm">
                  <InputGroup.Text className="bg-white text-muted" aria-hidden>
                    <FaSearch size={12} />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    placeholder="Name, object, or event..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    aria-label="Search workflows by name, object, or event"
                  />
                </InputGroup>
              </Col>
              <Col xs={12} sm="auto" md="auto" lg="auto" className="flex-grow-0">
                <PaginationOptions
                  labels={paginationOptionsLabels}
                  classes={paginationOptionsClasses}
                />
              </Col>
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">Object</Form.Label>
                <Form.Select
                  size="sm"
                  value={filterObjectName}
                  onChange={(e) => setFilterObjectName(e.target.value)}
                  aria-label="Filter by object"
                >
                  <option value="">All objects</option>
                  {objectNameOptions.map((name) => (
                    <option key={name} value={name}>
                      {resolveObjectLabel(name)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Label className="small text-muted mb-1">Status</Form.Label>
                <Form.Select
                  size="sm"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="">All statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={12} lg="auto" className="ms-lg-auto flex-grow-1">
                <Form.Label
                  className="small text-muted mb-1 d-block text-md-end text-lg-end"
                  aria-hidden
                >
                  &nbsp;
                </Form.Label>
                <small className="text-muted w-100 text-md-end text-lg-end d-block">
                  {filteredBody.length === body.length
                    ? `Showing ${filteredBody.length} workflow${filteredBody.length !== 1 ? "s" : ""}`
                    : `Showing ${filteredBody.length} of ${body.length} workflows`}
                </small>
              </Col>
            </Row>

            <div className="table-responsive">
              <Table hover className="mb-0">
                <TableHeader />
                <TableBody />
              </Table>
            </div>

            <div className="mt-3 d-flex justify-content-center">
              <Pagination />
            </div>
          </DatatableWrapper>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleDeleteCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the workflow <strong>"{selectedWorkflow?.workflow_name}"</strong>?
          <br />
          <small className="text-muted">This action cannot be undone.</small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WorkflowList;
